/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

/* eslint-disable no-unused-expressions,no-plusplus,no-bitwise,no-param-reassign,no-mixed-operators,no-console */
import { IInternalCrosstabHeaderCell, IInternalCrosstabHeaderRow } from '../types/CrosstabVisualizationTypes';

export function applyAdditionalLookAndFeelReact(headerRows: IInternalCrosstabHeaderRow[], forHorizontal: boolean) {

    headerRows.forEach((_row, rowIndex) => {
        const row = _row;
        const cells = row.nodes;
        cells.forEach((_cell, cIndex) => {
            const cell = _cell;
            if (!cell) {
                return;
            }
            // mark if this row contains total cells
            if (!row.isTotalRow && cell.isTotal) {
                row.isTotalRow = true;
            }
            // if the next to last cell in the row is total cell then mark the last one as total cell as well.
            const isTheLastCellInARow = cIndex + 1 === row.nodes.length;
            if (isTheLastCellInARow) {
                let upperCell = cells[cIndex - 1];

                // the upper (or parent) cell might be empty in some cases, but it actually means we need to look up
                // for upper cell to the left, so let's go as left as we can:
                if (!upperCell) {
                    let k :number = rowIndex - 1;
                    while (!upperCell && k >= 0) {
                        upperCell = headerRows[k].nodes[cIndex - 1];
                        k--;
                    }
                }

                const upperCellIsTotalCell = upperCell ? upperCell.isTotal : false;
                if (upperCellIsTotalCell) {
                    cell.isTotal = true;
                }
            }
        });
    });

    if (!forHorizontal) {

        let firstTotalRowId = -1;
        headerRows.forEach((_row, rIndex) => {
            const currentRow = _row;
            const nextRow = headerRows[rIndex + 1];
            const prevRow = headerRows[rIndex - 1];
            const cells = currentRow.nodes;
            let anyCellInCurrentRowHasSpan = false;
            cells.forEach((_cell, cIndex) => {
                if (!_cell) return;
                const currentCell = _cell;
                const currentCellSpan = currentCell.span ? currentCell.span : 1;
                anyCellInCurrentRowHasSpan = currentCellSpan > 0;
                // JRS-14959 & JRS-15048 -- Start
                const isTherePreviousCell = cIndex > 0;
                if (currentRow.isTotalRow && isTherePreviousCell) {
                    const prevCell = currentRow.nodes[cIndex - 1];
                    if (prevCell === null) {
                        if (firstTotalRowId !== -1) {
                            const firstTotalRow = headerRows[firstTotalRowId];
                            const prevCellOnFirstTotalRow = firstTotalRow.nodes[cIndex - 1];
                            if (prevCellOnFirstTotalRow && prevCellOnFirstTotalRow.isTotal && prevRow) {
                                const currentCellOnPrevRow = prevRow.nodes[cIndex];
                                if (currentCellOnPrevRow && currentCellOnPrevRow.isTotal && !currentCell.isTotal) {
                                    currentCell.isTotal = true;
                                    currentRow.isTotalRow = true;
                                }
                            }
                        }
                    } else if (prevCell.isTotal) {
                        currentCell.isTotal = true;
                        currentRow.isTotalRow = true;
                        if (firstTotalRowId === -1) {
                            firstTotalRowId = rIndex;
                        }
                    }
                }

                if (currentCell.isTotal) {
                    if (anyCellInCurrentRowHasSpan && currentRow.isTotalRow) {
                        for (let m = rIndex + 1; m < rIndex + currentCellSpan; m++) {
                            const rowWithSpanCells = headerRows[m];
                            for (let k = cIndex + 1; k < cells.length; k++) {
                                const cellWithSpan = rowWithSpanCells.nodes[k];
                                if (cellWithSpan !== null && !cellWithSpan.isTotal) {
                                    cellWithSpan.isTotal = true;
                                    if (!rowWithSpanCells.isTotalRow) {
                                        rowWithSpanCells.isTotalRow = true;
                                    }
                                }
                            }
                        }
                    }
                }
                // JRS-14959 & JRS-15048-- End

                if (currentCellSpan > 1) {
                    // JRS-14959 - Added additional condition separated from the above if.
                    const isLastCell = cIndex !== cells.length - 1;
                    if (isLastCell) {
                        const index = rIndex + currentCellSpan - 1;
                        headerRows[index].rowMemberLast = true;
                    }
                    if (!_row.rowMember) {
                        _row.rowGroup = true;
                        _row.rowMember = false;
                    }
                } else if (!_row.rowGroup) {
                    _row.rowMember = true;
                    _row.rowGroup = false;
                }
            });

            if (!anyCellInCurrentRowHasSpan) {
                if (prevRow) {
                    if (prevRow.rowMemberLast && !currentRow.isTotalRow) {
                        currentRow.rowMemberLast = true;
                    }
                }
            }

            if (nextRow) {
                if (!currentRow.isTotalRow && nextRow.isTotalRow) {
                    currentRow.rowMemberLast = true;
                }
            }
        });

        headerRows.forEach((_row, rIndex) => {
            const row = _row;
            let rowSpanCount = 0;
            let nonRowSpanCount = 0;
            const nextRow = headerRows[rIndex + 1];
            const prevRow = headerRows[rIndex - 1];

            const cells = row.nodes;
            const firstCell = cells[0];
            const firstCellHasSpanValue = firstCell && firstCell.span !== undefined;

            const isLastRow = rIndex === headerRows.length - 1;

            cells.forEach((cell, cIndex) => {
                const isLastCell = cIndex === cells.length - 1;

                if (!isLastRow && isLastCell && !firstCellHasSpanValue && !row.rowGroup) {
                    const prevCell = cells[cIndex - 1];
                    const prevCellLabel = prevCell && prevCell.label;
                    const prevCellOnNextRow = nextRow.nodes[cIndex - 1];
                    const prevCellOnNextRowLabel = prevCellOnNextRow && prevCellOnNextRow.label;

                    const labelsMatch = prevCellLabel === prevCellOnNextRowLabel;
                    if (!labelsMatch) {
                        row.rowMemberLast = true;
                    }
                }
                if (cell === null || cell && cell.span && cell.span > 0) {
                    rowSpanCount++;
                } else {
                    nonRowSpanCount++;
                }
            });
            // JRS-15096 & JRS-15212: Check if row is
            // 1) 1) one or more cells in the row have a rowspan,
            // 2) there are two or more remaining cells in the same row that have no rowspan (are visual members)
            if (!row.isTotalRow && rowSpanCount >= 1 && nonRowSpanCount >= 2) {
                const nextToLastCell = cells[row.nodes.length - 2];
                const nextToLastCellLabel = nextToLastCell && nextToLastCell.label;
                const nextToLastCellOnPrevRow = prevRow.nodes[row.nodes.length - 2];
                const nextToLastCellOnPrevRowLabel = nextToLastCellOnPrevRow && nextToLastCellOnPrevRow.label;

                const labelsMatch = nextToLastCellLabel === nextToLastCellOnPrevRowLabel;
                if (row.rowGroup || !labelsMatch) {
                    row.rowSingleMember = true;
                }
            }

            // JRS-15097: Check if the row is the only member and add rowMemberLast
            // JRS-15096: Check if row
            // 1)  is not a totals row,
            // 2) no cells in the row have rowspans, and
            // 3) the row contains two or more field or measure cells
            const firstCellOnNextRow = nextRow && nextRow.nodes[0];
            const rowWithCells = cells.length > 1;
            const firstCellLabel = firstCell && firstCell.label;
            const firstCellOnNextRowLabel = firstCellOnNextRow && firstCellOnNextRow.label;
            const labelsMatch = firstCellLabel === firstCellOnNextRowLabel;

            if (row && rowSpanCount === 0 && rowWithCells && !isLastRow && nextRow && !labelsMatch) {
                row.rowMemberLast = true;
            } // JRS-15096
            // JRS-15096
            const firstCellSpan = firstCell && firstCell.span ? firstCell.span : 0;
            if (rowSpanCount === 1 && firstCellSpan > 0 && cells.length > 2) {
                // JRS-14959 - Added additional condition separated from the above if.
                for (let k = rIndex + 1; k <= rIndex + firstCellSpan - 1; k++) {
                    // JRS-14908 & JRS-14869: Adding additional check to add rowMemberLast class.
                    let innerTypeCount = 0;
                    const rowK = headerRows[k];
                    for (let m = 0; m < rowK.nodes.length; m++) {
                        const rowKCellM = rowK.nodes[m];
                        rowKCellM === null && innerTypeCount++;
                        const rowKCellMSpan = firstCell && firstCell.span ? firstCell.span : 0;
                        rowKCellMSpan > 0 && innerTypeCount++;
                    }
                    if (rowK && !rowK.rowGroup && innerTypeCount === 1) {
                        rowK.rowMemberLast = true;
                    }
                }
            }
        });
    }
}

export function obtainRenderVersion(headerRows: IInternalCrosstabHeaderRow[], merged: boolean) {
    const res: IInternalCrosstabHeaderRow[] = headerRows.map((row) => ({
        ...row,
        nodes: row.nodes.map((node) => ({ ...node } as IInternalCrosstabHeaderCell))
    }));

    let node,
        currentNode,
        currentCloneNode,
        cloneNode;

    if (headerRows[0]) {
        for (let cIndex = 0; cIndex < headerRows[0].nodes.length; cIndex += 1) {
            currentNode = headerRows[0].nodes[cIndex];
            currentCloneNode = res[0].nodes[cIndex];

            for (let rIndex = 1; rIndex < headerRows.length; rIndex += 1) {
                node = headerRows[rIndex].nodes[cIndex];
                cloneNode = res[rIndex].nodes[cIndex];

                if (node && currentNode && currentCloneNode && cloneNode) {
                    if (currentNode === node) {
                        if (merged) {
                            currentCloneNode.span += 1;
                            res[rIndex].nodes[cIndex] = null;
                        } else {
                            cloneNode.expandable = false;
                        }
                    } else {
                        currentNode = node;
                        currentCloneNode = cloneNode;
                    }
                }
            }
        }
    }

    return res;
}
